
import { useEffect, useCallback, useState, useRef } from 'react';
import { useNavigate } from 'react-router';
import { useAuth } from '@/lib/auth-context';
import { api, setLivePrayerRequest, setLivePraiseRequest } from '@/lib/api';
import { useProducerQueue } from '@/hooks/useProducerQueue';
import { useClipboard } from '@/hooks/useClipboard';
import { ShuffleLayout } from '@/components/producer';
import { PrayerEditor } from '@/components/moderation/PrayerEditor';
import { Button, Card } from '@/components/ui';
import { ToastProvider, useToast } from '@/components/ui/Toast';
import type { PrayerRequest, UpdatePrayerRequest } from '@/types';

function ProducerDashboardContent() {
  const { addToast } = useToast();
  const { user } = useAuth();
  const currentUserId = user?.id ?? null;
  const { copySync } = useClipboard();
  const [shuffleActiveQueue, setShuffleActiveQueue] = useState<'prayer' | 'praise' | 'combined'>(() => {
    if (typeof window === 'undefined') return 'prayer';
    const v = localStorage.getItem('producer_shuffle_active_queue');
    if (v === 'praise' || v === 'combined') return v;
    return 'prayer';
  });
  const [editingPrayer, setEditingPrayer] = useState<PrayerRequest | null>(null);
  const [isSavingPrayer, setIsSavingPrayer] = useState(false);
  const editingPrayerIdRef = useRef<string | null>(null);

  const [token, setToken] = useState<string | undefined>(undefined);
  useEffect(() => {
    const storedToken = localStorage.getItem('access_token') || undefined;
    setToken(storedToken);
  }, []);

  const {
    prayerQueue,
    praiseQueue,
    prayerQueueGrouped,
    praiseQueueGrouped,
    isLoading,
    error,
    isConnected,
    connectionState,
    lastUpdate,
    newItemsCount,
    removeFromQueue,
    clearQueue,
    refresh,
    clearNewItems,
    exportQueue,
    getExportText,
    shufflePrayer,
    shufflePraise,
    shuffleCombined,
    shufflePrayerIntervalSeconds,
    shufflePraiseIntervalSeconds,
    shuffleCombinedIntervalSeconds,
    setShuffleState,
    livePrayerRequestId,
    livePraiseRequestId,
    liveCombinedRequestId,
    liveCombinedQueueType,
    shufflePrayerNextRequestId,
    shufflePraiseNextRequestId,
    shufflePrayerNextIds,
    shufflePraiseNextIds,
    shuffleCombinedNextIds,
    liveIsManualOverridePrayer,
    liveIsManualOverridePraise,
    setLivePrayerRequestId,
    setLivePraiseRequestId,
    setLiveCombinedRequestId,
    markLiveAsManualOverridePrayer,
    markLiveAsManualOverridePraise,
    editingBy,
    sendMessage,
    lastPongAt,
  } = useProducerQueue({
    token,
    activeQueue: shuffleActiveQueue,
  });

  const handleShuffleActiveQueueChange = useCallback((queue: 'prayer' | 'praise' | 'combined') => {
    setShuffleActiveQueue(queue);
    if (typeof window !== 'undefined') {
      localStorage.setItem('producer_shuffle_active_queue', queue);
    }
  }, []);

  // Handle remove from queue (manual only; displayed items stay in queue until removed)
  const handleRemove = useCallback(async (prayerId: string) => {
    const success = await removeFromQueue(prayerId);
    if (success) {
      addToast('Item removed from queue', 'success');
    } else {
      addToast('Failed to remove item', 'error');
    }
  }, [removeFromQueue, addToast]);

  // Clear displayed cards only; pending items stay in the queue
  const handleClearPrayerQueue = useCallback(async () => {
    const success = await clearQueue('prayer');
    if (success) {
      addToast('Displayed prayer cards cleared (pending kept)', 'success');
    } else {
      addToast('Failed to clear displayed prayer cards', 'error');
    }
  }, [clearQueue, addToast]);

  const handleClearPraiseQueue = useCallback(async () => {
    const success = await clearQueue('praise');
    if (success) {
      addToast('Displayed answered-prayer cards cleared (pending kept)', 'success');
    } else {
      addToast('Failed to clear displayed praise cards', 'error');
    }
  }, [clearQueue, addToast]);

  // Handle display live (prayer): fans out to prayer + combined when those shuffles are on
  const handleDisplayLive = useCallback(async (prayerRequestId: string) => {
    const { data, error } = await setLivePrayerRequest(prayerRequestId);
    if (error) {
      addToast(error, 'error');
      return;
    }
    if (data?.success) {
      if (shufflePrayer || !shuffleCombined) {
        setLivePrayerRequestId(prayerRequestId);
      }
      if (shuffleCombined) {
        setLiveCombinedRequestId(prayerRequestId);
      }
      markLiveAsManualOverridePrayer();
      addToast('Prayer request is now showing live', 'success');
    }
  }, [
    addToast,
    setLivePrayerRequestId,
    setLiveCombinedRequestId,
    markLiveAsManualOverridePrayer,
    shufflePrayer,
    shuffleCombined,
  ]);

  // Handle display live (praise): fans out to praise + combined when those shuffles are on
  const handleDisplayLivePraise = useCallback(async (prayerRequestId: string) => {
    const { data, error } = await setLivePraiseRequest(prayerRequestId);
    if (error) {
      addToast(error, 'error');
      return;
    }
    if (data?.success) {
      if (shufflePraise || !shuffleCombined) {
        setLivePraiseRequestId(prayerRequestId);
      }
      if (shuffleCombined) {
        setLiveCombinedRequestId(prayerRequestId);
      }
      markLiveAsManualOverridePraise();
      addToast('Answered prayer is now showing live', 'success');
    }
  }, [
    addToast,
    setLivePraiseRequestId,
    setLiveCombinedRequestId,
    markLiveAsManualOverridePraise,
    shufflePraise,
    shuffleCombined,
  ]);

  // Temporarily hidden — restore when Live Display returns
  // const handleOpenLiveDisplay = useCallback(() => {
  //   if (typeof window === 'undefined') return;
  //   window.open('/live-display', '_blank', 'noopener,noreferrer');
  // }, []);

  // Handle export
  const handleExport = useCallback(async (
    type: 'prayer' | 'praise' | 'all',
    options: { groupByCategory: boolean; includeCategoryLabels: boolean }
  ) => {
    return exportQueue(type, options);
  }, [exportQueue]);

  // Open editor and claim editing (same as Submissions page)
  const handleEdit = useCallback(
    (prayer: PrayerRequest) => {
      editingPrayerIdRef.current = prayer.id;
      sendMessage({ action: 'start_editing', prayer_id: prayer.id });
      setEditingPrayer(prayer);
    },
    [sendMessage]
  );

  // Close editor and release editing
  const handleCloseEditor = useCallback(() => {
    const prayerId = editingPrayerIdRef.current;
    if (prayerId) {
      sendMessage({ action: 'stop_editing', prayer_id: prayerId });
      editingPrayerIdRef.current = null;
    }
    setEditingPrayer(null);
  }, [sendMessage]);

  // On unmount, clear editing presence if editor was open
  useEffect(() => {
    return () => {
      const prayerId = editingPrayerIdRef.current;
      if (prayerId) {
        sendMessage({ action: 'stop_editing', prayer_id: prayerId });
      }
    };
  }, [sendMessage]);

  // Handle save from queue editor
  const handleSavePrayer = useCallback(
    async (id: string, data: UpdatePrayerRequest) => {
      setIsSavingPrayer(true);
      try {
        const response = await api.put<PrayerRequest>(`/prayer-requests/${id}`, data);
        if (response.error) {
          addToast(response.error, 'error');
          return;
        }
        addToast('Prayer updated', 'success');
        sendMessage({ action: 'stop_editing', prayer_id: id });
        editingPrayerIdRef.current = null;
        setEditingPrayer(null);
        // Rely on WebSocket (PRAYER_UPDATED / PRAYER_UNQUEUED) to update queue so removal isn't overwritten by a late refresh()
      } catch (err) {
        addToast(err instanceof Error ? err.message : 'Failed to update prayer', 'error');
      } finally {
        setIsSavingPrayer(false);
      }
    },
    [addToast, refresh, sendMessage]
  );

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore if typing in input
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }

      // P - Copy prayer queue (sync so clipboard runs in same user gesture)
      if (e.key.toLowerCase() === 'p' && !e.ctrlKey && !e.metaKey) {
        const text = getExportText('prayer', { groupByCategory: true, includeCategoryLabels: true });
        const ok = copySync(text);
        addToast(ok ? 'Prayer queue copied!' : 'Failed to copy to clipboard', ok ? 'success' : 'error');
        return;
      }

      // R - Copy praise queue (R for Reports/Praise)
      if (e.key.toLowerCase() === 'r' && !e.ctrlKey && !e.metaKey) {
        const text = getExportText('praise', { groupByCategory: true, includeCategoryLabels: true });
        const ok = copySync(text);
        addToast(ok ? 'Praise queue copied!' : 'Failed to copy to clipboard', ok ? 'success' : 'error');
        return;
      }

      // A - Copy all
      if (e.key.toLowerCase() === 'a' && !e.ctrlKey && !e.metaKey) {
        const text = getExportText('all', { groupByCategory: true, includeCategoryLabels: true });
        const ok = copySync(text);
        addToast(ok ? 'All items copied!' : 'Failed to copy to clipboard', ok ? 'success' : 'error');
        return;
      }

      // F5 or Ctrl+R to refresh
      if (e.key === 'F5' || (e.ctrlKey && e.key.toLowerCase() === 'r')) {
        e.preventDefault();
        refresh();
        addToast('Refreshing...', 'info');
        return;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [getExportText, copySync, refresh, addToast]);

  // Error state
  if (error && !isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-3.5rem)] p-4">
        <Card className="text-center max-w-md" padding="lg">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
            <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white mb-2">
            Connection Error
          </h2>
          <p className="text-slate-500 dark:text-slate-400 mb-4">{error}</p>
          <Button variant="ghost" onClick={refresh}>
            Try Again
          </Button>
        </Card>
      </div>
    );
  }

  const shuffleLayoutProps = {
    prayerQueue,
    praiseQueue,
    prayerQueueGrouped,
    praiseQueueGrouped,
    isLoading,
    connectionState,
    lastPongAt,
    lastUpdate,
    newItemsCount,
    clearNewItems,
    getExportText,
    refresh,
    shufflePrayer,
    shufflePraise,
    shuffleCombined,
    shufflePrayerIntervalSeconds,
    shufflePraiseIntervalSeconds,
    shuffleCombinedIntervalSeconds,
    setShuffleState,
    livePrayerRequestId,
    livePraiseRequestId,
    liveCombinedRequestId,
    liveCombinedQueueType,
    shufflePrayerNextRequestId,
    shufflePraiseNextRequestId,
    shufflePrayerNextIds,
    shufflePraiseNextIds,
    shuffleCombinedNextIds,
    liveIsManualOverridePrayer,
    liveIsManualOverridePraise,
    setLivePrayerRequestId,
    setLivePraiseRequestId,
    setLiveCombinedRequestId,
    markLiveAsManualOverridePrayer,
    markLiveAsManualOverridePraise,
    editingBy,
    addToast,
    copySync,
    onRemove: handleRemove,
    onClearPrayerQueue: handleClearPrayerQueue,
    onClearPraiseQueue: handleClearPraiseQueue,
    onDisplayLive: handleDisplayLive,
    onDisplayLivePraise: handleDisplayLivePraise,
    onEdit: handleEdit,
    currentUserId,
    // onOpenLiveDisplay: handleOpenLiveDisplay, // Temporarily hidden — restore when Live Display returns
    activeQueue: shuffleActiveQueue,
    onActiveQueueChange: handleShuffleActiveQueueChange,
  };

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <ShuffleLayout {...shuffleLayoutProps} />

      <PrayerEditor
        prayer={editingPrayer}
        isOpen={editingPrayer !== null}
        onClose={handleCloseEditor}
        onSave={handleSavePrayer}
        isSaving={isSavingPrayer}
      />

      {/* Connection status details below grid */}
      {!isConnected && (
        <div className="p-4 rounded-lg border border-yellow-200 dark:border-yellow-800 bg-yellow-50 dark:bg-yellow-900/20">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <div>
              <p className="font-medium text-yellow-800 dark:text-yellow-200">
                Real-time updates unavailable
              </p>
              <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                Working in offline mode. Changes will sync when connection is restored.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Wrap with ToastProvider
export default function ProducerPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Producer and admin only
  useEffect(() => {
    if (user && !['admin', 'producer'].includes(user.role)) {
      navigate('/unauthorized', { replace: true });
    }
  }, [user, navigate]);

  return (
    <ToastProvider>
      <ProducerDashboardContent />
    </ToastProvider>
  );
}
